/**
 * 前端直接调用代币相关API的工具函数
 * 替代 /api/proxy/token/* 代理调用
 */

import type { TokenEntity } from "@/types/odin-api";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import Decimal from "decimal.js";

const uiLog = createLogAdapter("lib/utils/token-api");

export type PlatformId =
	| "odinfun"
	| "fomowell"
	| "richswap"
	| "icpswap"
	| "okxweb3"
	| "bound";

type TracedRequestInit = RequestInit & {
	opentelemetry?: {
		ignore?: boolean;
		propagateContext?: boolean;
	};
};

export type TokenInfo = TokenEntity;

export interface PlatformInfo {
	id: PlatformId;
	name: string; // 平台名称
	price: number | null; // 价格（Sats）
	priceUsd?: number | null; // 原始美元价格（用于 ICPSwap 等需要二次换算的平台）
	priceUnit: string; // 价格单位
	buyLink: string; // 购买链接
	logoSrc: string; // 平台 logo
	isLoading?: boolean; // 加载状态
	error?: string; // 错误信息
}

export interface ExtendedTokenInfo extends TokenInfo {
	platforms: PlatformInfo[]; // 多平台信息数组
}

// 平台配置
const PLATFORM_CONFIG = {
	odinfun: {
		id: "odinfun" as PlatformId,
		name: "OdinFun",
		buyLink: "https://www.odin.baby/plaza/token/2rfi",
		priceUnit: "Sats",
		logoSrc: "/swaplogo/odinfun.png",
	},
	fomowell: {
		id: "fomowell" as PlatformId,
		name: "FomoWell",
		buyLink: "https://btc.fomowell.com/token/66",
		priceUnit: "Sats",
		logoSrc: "/swaplogo/fomowell.png",
	},
	richswap: {
		id: "richswap" as PlatformId,
		name: "RichSwap",
		buyLink:
			"https://www.richswap.io/swap?coinA=BTC&coinB=BABYODIN%E2%80%A2ID%E2%80%A2NWKQ%E2%80%A2ODIN",
		priceUnit: "Sats",
		logoSrc: "/swaplogo/richswap.png",
	},
	icpswap: {
		id: "icpswap" as PlatformId,
		name: "IcpSwap",
		buyLink:
			"https://app.icpswap.com/info-swap/token/details/n4v6w-viaaa-aaaar-qbrvq-cai",
		priceUnit: "Sats",
		logoSrc: "/swaplogo/icpswap.png",
	},
	okxweb3: {
		id: "okxweb3" as PlatformId,
		name: "OKX Web3",
		buyLink:
			"https://web3.okx.com/zh-hans/token/ethereum/0xc5d8bc1eefbfbf354ac962f3e3bcf0b618198bb1",
		priceUnit: "USD",
		logoSrc: "/swaplogo/okx-web3.png",
	},
	bound: {
		id: "bound" as PlatformId,
		name: "Bound",
		buyLink:
			"https://app.bound.exchange/trade?inputCurrency=905743:1874&outputCurrency=0:0",
		priceUnit: "Sats",
		logoSrc: "/swaplogo/bound.svg",
	},
};

const ICPSWAP_PID = "n4v6w-viaaa-aaaar-qbrvq-cai";

interface IcpSwapTokenDataResponse {
	code: number;
	message: string | null;
	data: {
		price?: string;
		ledgerId?: string;
	} | null;
}

interface OkxMarketPriceProxyResponse {
	code: number;
	message: string;
	data: {
		priceUsd?: number;
		time?: string | null;
		chainIndex?: string;
		tokenContractAddress?: string;
	} | null;
}

interface BoundPriceProxyResponse {
	code: number;
	message: string;
	data: {
		priceInSats?: number;
		priceUsd?: number | null;
		tokenId?: string;
		spacedRune?: string;
		updatedAt?: number | null;
	} | null;
}

interface BoundPrice {
	priceInSats: number;
	priceUsd: number | null;
}

/**
 * 获取单个代币信息
 * @param tokenId 代币ID
 * @returns Promise<TokenInfo>
 */
export async function fetchTokenInfo(tokenId: string): Promise<TokenInfo> {
	const response = await fetch(`https://api.odin.fun/v1/token/${tokenId}`, {
		method: "GET",
		headers: {
			Accept: "application/json",
			"User-Agent":
				"Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
		},
	});

	if (!response.ok) {
		throw new Error(
			`Failed to fetch token ${tokenId}: ${response.status} ${response.statusText}`,
		);
	}

	return response.json();
}

/**
 * 批量获取多个代币信息
 * @param tokenIds 代币ID数组
 * @returns Promise<TokenInfo[]>
 */
export async function fetchMultipleTokenInfo(
	tokenIds: string[],
): Promise<TokenInfo[]> {
	const promises = tokenIds.map((tokenId) =>
		fetchTokenInfo(tokenId).catch((error) => {
			uiLog.error(`Error fetching token ${tokenId}:`, error);
			return null;
		}),
	);

	const results = await Promise.all(promises);
	return results.filter((token): token is TokenInfo => token !== null);
}

/**
 * 获取2RFI代币信息（常用）
 * @returns Promise<TokenInfo>
 */
export async function fetch2RFITokenInfo(): Promise<TokenInfo> {
	return fetchTokenInfo("2rfi");
}

/**
 * 从 RichSwap 获取 BabyOdin 价格（通过后端代理）
 * @returns Promise<number | null> 价格（Sats）
 */
export async function fetchRichSwapPrice(): Promise<number | null> {
	try {
		const response = await fetch(
			"/api/proxy/richswap/price?rune=BABYODIN%E2%80%A2ID%E2%80%A2NWKQ%E2%80%A2ODIN",
			{
				method: "GET",
				headers: {
					Accept: "application/json",
				},
				opentelemetry: {
					ignore: true,
					propagateContext: false,
				},
			} satisfies TracedRequestInit,
		);

		if (!response.ok) {
			throw new Error(`RichSwap API error: ${response.status}`);
		}

		const data = await response.json();
		if (data.success && data.data?.price) {
			return data.data.price;
		}

		return null;
	} catch (error) {
		uiLog.error("Error fetching RichSwap price:", error);
		return null;
	}
}

/**
 * 从 ICPSwap 获取 BabyOdin 价格（返回美元价）
 * @returns Promise<number | null> 价格（USD）
 */
export async function fetchIcpSwapPriceUsd(): Promise<number | null> {
	try {
		const response = await fetch("/api/proxy/icpswap/token-data", {
			method: "POST",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ pid: ICPSWAP_PID }),
			opentelemetry: {
				ignore: true,
				propagateContext: false,
			},
		} satisfies TracedRequestInit);

		if (!response.ok) {
			throw new Error(`ICPSwap API error: ${response.status}`);
		}

		const data: IcpSwapTokenDataResponse = await response.json();
		if (data.code === 200 && data.data?.price) {
			return Number(data.data.price);
		}

		return null;
	} catch (error) {
		uiLog.error("Error fetching ICPSwap price:", error);
		return null;
	}
}

/**
 * 从 OKX Web3 获取 BabyOdin 价格（返回美元价）
 * @returns Promise<number | null> 价格（USD）
 */
export async function fetchOkxWeb3PriceUsd(): Promise<number | null> {
	try {
		const response = await fetch("/api/proxy/okx/market-price", {
			method: "GET",
			headers: {
				Accept: "application/json",
			},
			opentelemetry: {
				ignore: true,
				propagateContext: false,
			},
		} satisfies TracedRequestInit);

		if (!response.ok) {
			throw new Error(`OKX Web3 API error: ${response.status}`);
		}

		const data: OkxMarketPriceProxyResponse = await response.json();
		if (data.code === 0 && data.data?.priceUsd) {
			return Number(data.data.priceUsd);
		}

		return null;
	} catch (error) {
		uiLog.error("Error fetching OKX Web3 price:", error);
		return null;
	}
}

/**
 * 从 Bound 获取 BabyOdin 价格
 * @returns Promise<BoundPrice | null> 价格（Sats + USD）
 */
export async function fetchBoundPrice(): Promise<BoundPrice | null> {
	try {
		const response = await fetch("/api/proxy/bound/token-price", {
			method: "GET",
			headers: {
				Accept: "application/json",
			},
			opentelemetry: {
				ignore: true,
				propagateContext: false,
			},
		} satisfies TracedRequestInit);

		if (!response.ok) {
			throw new Error(`Bound API error: ${response.status}`);
		}

		const data: BoundPriceProxyResponse = await response.json();
		const priceInSats = Number(data.data?.priceInSats);
		const priceUsd =
			data.data?.priceUsd === null || data.data?.priceUsd === undefined
				? null
				: Number(data.data.priceUsd);

		if (data.code === 0 && Number.isFinite(priceInSats) && priceInSats > 0) {
			return {
				priceInSats,
				priceUsd:
					priceUsd !== null && Number.isFinite(priceUsd) && priceUsd > 0
						? priceUsd
						: null,
			};
		}

		return null;
	} catch (error) {
		uiLog.error("Error fetching Bound price:", error);
		return null;
	}
}

/**
 * 从 FomoWell 获取 BabyOdin 价格
 * @returns Promise<number | null> 价格（Sats）
 */
export async function fetchFomoWellPrice(): Promise<number | null> {
	try {
		// 动态导入 FomoWellBTCClient 避免服务端渲染问题
		const { FomoWellBTCClient } = await import(
			"@/lib/clients/FomoWellBTCClient"
		);

		const client = new FomoWellBTCClient();
		const result = await client.query_meme_token_price(66n);

		if ("Ok" in result) {
			return Number(result.Ok);
		} else {
			uiLog.error("FomoWell price error:", result.Err);
			return null;
		}
	} catch (error) {
		uiLog.error("Error fetching FomoWell price:", error);
		return null;
	}
}

/**
 * 获取 BabyOdin 在多个平台的价格信息
 * @returns Promise<ExtendedTokenInfo>
 */
export async function fetchMultiPlatformTokenInfo(): Promise<ExtendedTokenInfo> {
	try {
		// 并行获取所有平台的价格
		const [
			odinFunInfo,
			fomoWellPrice,
			richSwapPrice,
			icpSwapPriceUsd,
			okxWeb3PriceUsd,
			boundPrice,
		] = await Promise.allSettled([
			fetch2RFITokenInfo(),
			fetchFomoWellPrice(),
			fetchRichSwapPrice(),
			fetchIcpSwapPriceUsd(),
			fetchOkxWeb3PriceUsd(),
			fetchBoundPrice(),
		]);

		// 处理 OdinFun 数据
		const baseTokenInfo =
			odinFunInfo.status === "fulfilled"
				? odinFunInfo.value
				: {
						id: "2rfi",
						name: "BabyOdin",
						ticker: "BabyOdin",
						decimals: 8,
						divisibility: 8,
						price: undefined,
					};

		// 构建平台信息数组
		const platforms: PlatformInfo[] = [
			{
				...PLATFORM_CONFIG.odinfun,
				price: baseTokenInfo.price ? Number(baseTokenInfo.price) / 1000 : null, // 转换为 Sats
				priceUsd: null,
				isLoading: false,
				error:
					odinFunInfo.status === "rejected"
						? "Failed to fetch price"
						: undefined,
			},
			{
				...PLATFORM_CONFIG.fomowell,
				price:
					fomoWellPrice.status === "fulfilled" ? fomoWellPrice.value : null,
				priceUsd: null,
				isLoading: false,
				error:
					fomoWellPrice.status === "rejected"
						? "Failed to fetch price"
						: undefined,
			},
			{
				...PLATFORM_CONFIG.richswap,
				price:
					richSwapPrice.status === "fulfilled" ? richSwapPrice.value : null,
				priceUsd: null,
				isLoading: false,
				error:
					richSwapPrice.status === "rejected"
						? "Failed to fetch price"
						: undefined,
			},
			{
				...PLATFORM_CONFIG.icpswap,
				price: null,
				priceUsd:
					icpSwapPriceUsd.status === "fulfilled" ? icpSwapPriceUsd.value : null,
				isLoading: false,
				error:
					icpSwapPriceUsd.status === "rejected"
						? "Failed to fetch price"
						: undefined,
			},
			{
				...PLATFORM_CONFIG.okxweb3,
				price: null,
				priceUsd:
					okxWeb3PriceUsd.status === "fulfilled" ? okxWeb3PriceUsd.value : null,
				isLoading: false,
				error:
					okxWeb3PriceUsd.status === "rejected"
						? "Failed to fetch price"
						: undefined,
			},
			{
				...PLATFORM_CONFIG.bound,
				price:
					boundPrice.status === "fulfilled"
						? (boundPrice.value?.priceInSats ?? null)
						: null,
				priceUsd:
					boundPrice.status === "fulfilled"
						? (boundPrice.value?.priceUsd ?? null)
						: null,
				isLoading: false,
				error:
					boundPrice.status === "rejected" ? "Failed to fetch price" : undefined,
			},
		];

		// 格式化价格，保留3位小数；ICPSwap 先保留原始美元价，由展示层换算成 sats
		platforms.forEach((platform) => {
			if (platform.price !== null) {
				platform.price = Number(new Decimal(platform.price).toFixed(3));
			}
			if (platform.priceUsd !== null && platform.priceUsd !== undefined) {
				platform.priceUsd = Number(new Decimal(platform.priceUsd).toFixed(8));
			}
		});

		return {
			...baseTokenInfo,
			platforms,
		};
	} catch (error) {
		uiLog.error("Error fetching multi-platform token info:", error);
		throw error;
	}
}

/**
 * Search tokens on odin.fun
 * @param query Search query
 * @returns Promise<TokenInfo[]>
 */
export async function searchTokens(query: string): Promise<TokenInfo[]> {
	const encodedQuery = encodeURIComponent(query);
	const url = `https://api.odin.fun/v1/tokens?page=1&limit=5&sort=price%3Adesc&search=${encodedQuery}`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Accept: "application/json",
		},
	});

	if (!response.ok) {
		throw new Error(
			`Failed to search tokens: ${response.status} ${response.statusText}`,
		);
	}

	const json = await response.json();
	return json.data || [];
}
